import React, { FC, useEffect } from 'react';

import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router';

import { ActionButtonSizes, ActionButtonVariants } from 'components/action-button';
import { LoyaltyTierKey } from 'generated/graphql-gateway';
import { useDialogModal } from 'hooks/use-dialog-modal';
import { useMediaQuery } from 'hooks/use-media-query';
import { EVENT_PAGE_NAMES } from 'state/cdp/constants';
import { useLocationContext } from 'state/location';
import { useFeaturesEarningCalculations } from 'state/loyalty/hooks/use-feature-earning-calculations';
import { useFeaturesLoyaltyTiersContent } from 'state/loyalty/hooks/use-feature-loyalty-tiers-content';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { useLoyaltyUserTier } from 'state/loyalty/hooks/use-loyalty-user-tier';

import { IBannerReward, LoyaltyRewardsCarousel } from '../loyalty-rewards-carousel';
import { ILoyaltyTiersModalProps, LoyaltyTiersModal } from '../loyalty-tiers-modal';

import {
  AuthenticatedTierContainer,
  HowItWorksButton,
  StyledPointsDisplay,
  TierDescription,
  TierDetailsContainer,
  TierIcon,
  TierIconSmall,
  TierPointBar,
  TierPointsContainer,
  TierProgress,
  TierStart,
  TierTarget,
  UserCrownPointsContainer,
  UserTier,
} from './styled';
import { TimeUnit } from './types';

interface ILoyaltyTiersAuthenticatedBannerProps {
  compact?: boolean;
  displayRewardsLink?: boolean;
  hideCarouselRoutes?: string[];
  displayCarousel?: boolean;
}

let modalDismissed = false;

export const LoyaltyTiersBannerAuthenticated: FC<ILoyaltyTiersAuthenticatedBannerProps> = ({
  compact,
  displayRewardsLink,
  hideCarouselRoutes,
  displayCarousel = true,
}) => {
  const {
    loading: tiersContentLoading,
    data: tiersData,
    getContentForTier,
  } = useFeaturesLoyaltyTiersContent();
  const { loading: earningCalculationsLoading, getTierEarningData } =
    useFeaturesEarningCalculations();
  const { loyaltyUser } = useLoyaltyUser();
  const { loyaltyTierKey: userTier, pointsEarned, loyaltyTierChanged } = useLoyaltyUserTier();

  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const { setEventPageName } = useLocationContext();
  const isMobile = useMediaQuery('mobile');

  const [TierModal, openModal] = useDialogModal<{}, ILoyaltyTiersModalProps>({
    showCancel: true,
    Component: LoyaltyTiersModal,
    modalAppearanceEventMessage: 'Confirmation Modal: Loyalty Tier Changed',
    onConfirm: () => {
      modalDismissed = true;
    },
    onDismiss: () => {
      modalDismissed = true;
    },
  });

  useEffect(() => {
    // If tier changed and modal was not already dismissed
    if (loyaltyTierChanged && !modalDismissed) {
      openModal({
        type: loyaltyTierChanged,
      });
    }
  }, [formatMessage, loyaltyTierChanged, openModal, userTier]);

  if (!loyaltyUser || tiersContentLoading || earningCalculationsLoading) {
    return null;
  }

  const userPoints = loyaltyUser.points || 0;

  const entranceData = getTierEarningData(LoyaltyTierKey.TIER_2);

  let pointsTarget: number;
  let timeIntervalTarget;
  let timeUnitTarget: TimeUnit;

  switch (userTier) {
    case LoyaltyTierKey.TIER_1:
    default:
      pointsTarget = entranceData?.pointsNeeded || 1;
      timeIntervalTarget = entranceData?.pointsToEnterCalculationTimeInterval;
      timeUnitTarget =
        TimeUnit[entranceData?.pointsToEnterCalculationTimeUnit as keyof typeof TimeUnit];
      break;
    case LoyaltyTierKey.TIER_2:
      pointsTarget = entranceData?.pointsNeededToMaintain || 1;
      timeIntervalTarget = entranceData?.pointsToMaintainCalculationTimeInterval;
      timeUnitTarget =
        TimeUnit[entranceData?.pointsToMaintainCalculationTimeUnit as keyof typeof TimeUnit];
      break;
  }

  const tierConfig = tiersData?.loyaltyTiersUIConfig?.find(
    config => config?.loyaltyTiersUIConfigID === userTier
  );

  const carouselRewards = getContentForTier(userTier)?.loyaltyTiersCarouselRewards;

  // The carousel can be hidden with the prop or when the reward list is empty in Sanity
  const shouldRenderCarousel =
    displayCarousel &&
    carouselRewards?.length &&
    (hideCarouselRoutes || []).every(route => location.pathname.indexOf(route) === -1);

  // If Tiers have not been configured in Sanity, don't render anything
  if (!tierConfig) {
    return null;
  }

  const handleClick = () => {
    if (!displayRewardsLink) {
      setEventPageName(EVENT_PAGE_NAMES.MyBurgerKingLoyaltyTiers);
      return navigate(tiersData?.loyaltyTiersHowItWorksButtonLink?.locale!);
    }
    setEventPageName(EVENT_PAGE_NAMES.Rewards);
    navigate(tiersData?.loyaltyTiersRewardsButtonLink?.locale!);
  };

  const ActionButton = (
    <HowItWorksButton
      size={ActionButtonSizes.SMALL}
      data-testid="authenticated-tier-banner-how-it-works-button"
      variant={ActionButtonVariants.OUTLINE}
      onClick={handleClick}
      eventAttributes={{
        Name: displayRewardsLink ? 'Rewards' : 'How it Works',
        Path: location.pathname,
      }}
    >
      {displayRewardsLink
        ? tiersData?.loyaltyTiersRewardsButtonText?.locale
        : tiersData?.loyaltyTiersHowItWorksButtonText?.locale}
    </HowItWorksButton>
  );

  if (compact) {
    return (
      <AuthenticatedTierContainer data-testid="authenticated-tier-banner-compact">
        <TierDetailsContainer>
          <TierIconSmall
            image={tierConfig?.loyaltyTiersUIConfigIcon?.locale}
            alt=""
            objectFitContain
          />
          <UserTier accentColor={userTier === LoyaltyTierKey.TIER_2}>
            <UserCrownPointsContainer>
              <StyledPointsDisplay icon={null} value={userPoints} />
            </UserCrownPointsContainer>
          </UserTier>
          {!tiersContentLoading && isMobile && ActionButton}
        </TierDetailsContainer>
        {!tiersContentLoading && !isMobile && ActionButton}
      </AuthenticatedTierContainer>
    );
  }

  return (
    <AuthenticatedTierContainer data-testid="authenticated-tier-banner">
      <TierDetailsContainer>
        <TierIcon image={tierConfig?.loyaltyTiersUIConfigIcon?.locale} alt="" objectFitContain />
        <UserTier accentColor={userTier === LoyaltyTierKey.TIER_2}>
          {formatMessage(
            { id: 'loyaltyTiersYouAre' },
            { tierName: tierConfig?.loyaltyTiersUIConfigName?.locale }
          )}
          <UserCrownPointsContainer>
            <StyledPointsDisplay icon={null} value={userPoints} />
          </UserCrownPointsContainer>
        </UserTier>
        {!tiersContentLoading && isMobile && ActionButton}
      </TierDetailsContainer>
      <TierPointsContainer>
        <TierDescription>
          {formatMessage(
            { id: `loyaltyTiersLabel${timeUnitTarget}${userTier}` },
            {
              // Number of points
              points: pointsTarget,
              // How points are named (eg. Crowns)
              pointsLabel: formatMessage({ id: 'points' }),
              // The amount of time (eg. 3)
              timeCount: timeIntervalTarget,
              // The next tier name (eg. Superking)
              tierName: getContentForTier(LoyaltyTierKey.TIER_2)?.loyaltyTiersUIConfigName?.locale,
            }
          )}
        </TierDescription>
        <TierPointBar>
          <TierProgress progress={Math.min((pointsEarned / pointsTarget) * 100, 100)} />
          <TierStart value={0} />
          <TierTarget value={pointsTarget} />
        </TierPointBar>
      </TierPointsContainer>
      {!tiersContentLoading && !isMobile && ActionButton}
      {shouldRenderCarousel ? (
        <LoyaltyRewardsCarousel
          rewards={carouselRewards as unknown as IBannerReward[]}
          currentUserTier={userTier}
        />
      ) : null}
      <TierModal modalType={loyaltyTierChanged} />
    </AuthenticatedTierContainer>
  );
};
